/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * checkInRules
 * ------------
 * The pure half of the check-in desk: when an event is open for check-in, how
 * late a scan is, and what to call the activity on a pass. No requests, no
 * React — everything here takes `now` as an argument so it can be tested at
 * any hour.
 *
 * All event times are the stored UTC instants. They are compared as instants
 * and only converted to the event's own zone for display, so a desk in another
 * zone from the event still opens at the right moment.
 */

import envConfig from '../../../config/envConfig';
import { parseStoredUtc, utcToWallClock } from '../../../utils/zonedDateTime';

/** Check-in opens this long before an event starts (REACT_APP_CHECKIN_OPENS_BEFORE_MIN, default 60). */
export const OPENS_BEFORE_MIN = envConfig.checkInOpensBeforeMin;

/**
 * After start + this, a check-in asks the organizer first
 * (REACT_APP_CHECKIN_LATE_AFTER_MIN). null = every check-in after the start asks.
 */
export const LATE_AFTER_MIN = envConfig.checkInLateAfterMin;

/** Check-out stops this long after the end; null = never (REACT_APP_CHECKOUT_CLOSES_AFTER_END_MIN). */
export const CHECKOUT_CLOSES_AFTER_END_MIN = envConfig.checkOutClosesAfterEndMin;

/**
 * A pass scanned again within this long of its check-in is a double scan at
 * the entrance, not someone leaving (REACT_APP_CHECKIN_RESCAN_MIN, default 10).
 */
export const RESCAN_MIN = envConfig.checkInRescanMin;

const MINUTE_MS = 60 * 1000;

/** Events in these states never take check-ins, whatever the clock says. */
export const CLOSED_STATUSES = new Set(['DRAFT', 'CANCELLED']);

/**
 * The id behind a column value.
 *
 * /table/fetch hands a foreign key back as `{ related_table_name, data: { id } }`
 * and a plain column as the bare value, so every id read off a fetched row goes
 * through here rather than guessing which shape it got.
 */
export function idOf(value) {
  if (value && typeof value === 'object') return value.data?.id ?? null;
  return value ?? null;
}

/** "YYYY-MM-DD" of an instant as seen in `timeZone`. */
function dayIn(instant, timeZone) {
  return utcToWallClock(instant.toISOString(), timeZone).slice(0, 10);
}

/**
 * Where `now` falls for one event.
 *
 * - `not_open` before start − OPENS_BEFORE_MIN, and for draft or cancelled events;
 * - `on_time`  from then until start + LATE_AFTER_MIN (until the start, when that is not set);
 * - `late`     after that, up to the end;
 * - `ended`    after the end.
 *
 * `checkOutClosed` is true once CHECKOUT_CLOSES_AFTER_END_MIN has passed after
 * the end (never, when that is not set).
 *
 * `late` and `ended` still check people in — they only add a confirmation.
 * `isOpen` is what puts an event on the desk: from opening until the end of the
 * calendar day it ends on, in its own zone, so stragglers after the finish can
 * still be checked in and out, and yesterday's event drops off overnight.
 */
export function eventWindow(event, now) {
  const start = parseStoredUtc(event?.start_datetime);
  const end = parseStoredUtc(event?.end_datetime);
  if (!start || !end) return { isOpen: false, phase: 'not_open', opensAt: null, start, end };

  const opensAt = new Date(start.getTime() - OPENS_BEFORE_MIN * MINUTE_MS);
  const lateAt = new Date(start.getTime() + (LATE_AFTER_MIN ?? 0) * MINUTE_MS);
  const at = now.getTime();

  let phase = 'on_time';
  if (at < opensAt.getTime()) phase = 'not_open';
  else if (at > end.getTime()) phase = 'ended';
  else if (at > lateAt.getTime()) phase = 'late';

  const status = String(event.status || '').toUpperCase();
  const zone = event.timezone;
  const isOpen = !CLOSED_STATUSES.has(status)
    && phase !== 'not_open'
    && dayIn(now, zone) <= dayIn(end, zone);

  return {
    isOpen,
    phase: CLOSED_STATUSES.has(status) ? 'not_open' : phase,
    opensAt,
    lateAt,
    start,
    end,
    minutesLate: Math.max(0, Math.round((at - start.getTime()) / MINUTE_MS)),
    minutesAfterEnd: Math.max(0, Math.round((at - end.getTime()) / MINUTE_MS)),
    checkOutClosesAt: CHECKOUT_CLOSES_AFTER_END_MIN == null
      ? null : new Date(end.getTime() + CHECKOUT_CLOSES_AFTER_END_MIN * MINUTE_MS),
    checkOutClosed: CHECKOUT_CLOSES_AFTER_END_MIN != null
      && at > end.getTime() + CHECKOUT_CLOSES_AFTER_END_MIN * MINUTE_MS,
  };
}

/**
 * Only the events this organizer's organization created.
 *
 * Row-level security is not enough on its own here: it also shows people rows
 * they own as a participant, so an organizer who registered for someone else's
 * event can read that event and their own pass for it. The desk must never
 * check anyone in to another organization's event, so it asks the question
 * itself, from the column that says whose event it is.
 */
export function ownEvents(events, organizationId) {
  if (organizationId == null) return [];
  return (events || []).filter((event) => Number(idOf(event.creator_organization_id)) === Number(organizationId));
}

/** The events the desk accepts passes for right now, soonest start first. */
export function openEvents(events, now) {
  return (events || [])
    .filter((event) => eventWindow(event, now).isOpen)
    .sort((a, b) => parseStoredUtc(a.start_datetime) - parseStoredUtc(b.start_datetime));
}

/**
 * The participant's name from a pass's `user_info` ("Arjun Rao"), or '' when
 * the pass has none — older passes, or accounts with no name. The desk then
 * falls back to the passcode.
 */
export function nameOf(userInfo) {
  const info = infoOf(userInfo);
  return [info?.first_name, info?.last_name].map((part) => String(part || '').trim()).filter(Boolean).join(' ');
}

/**
 * The pass's `user_info` as an object: what was known about the person when
 * they registered (name, username, email, phone). Room's jsonb comes back as
 * an object, but a string is parsed too. Empty or unreadable → null: a pass
 * issued before these details were saved simply has none.
 */
export function infoOf(userInfo) {
  let info = userInfo;
  if (typeof info === 'string') {
    try { info = JSON.parse(info); } catch (error) { return null; }
  }
  return info && typeof info === 'object' && Object.keys(info).length ? info : null;
}

/** "6:12 am" for a stored UTC instant, shown in the event's zone. */
export function clockIn(value, timeZone) {
  const instant = value instanceof Date ? value : parseStoredUtc(value);
  if (!instant) return '';
  const wall = utcToWallClock(instant.toISOString(), timeZone);
  const [hour, minute] = wall.slice(11, 16).split(':').map(Number);
  if (Number.isNaN(hour)) return '';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'am' : 'pm'}`;
}

/**
 * What to call the activity a pass is for.
 *
 * The registration names it; nothing at the desk is compared against it. A
 * SEQUENTIAL event leaves the registration's activity empty on purpose (one
 * entry covers every leg), and a DEFAULT event has exactly one activity.
 */
export function activityLabel(event, registration, activitiesById) {
  if (String(event?.activity_mode || '').toUpperCase() === 'SEQUENTIAL') return 'All legs';
  const activityId = idOf(registration?.event_activity_id);
  if (activityId != null && activitiesById?.[activityId]) return activitiesById[activityId].name;
  const own = Object.values(activitiesById || {}).filter(
    (activity) => idOf(activity.event_id) === event?.id
  );
  return own.length === 1 ? own[0].name : '';
}
